import { Group, Portal, SegmentedControl, Stack, Text } from '@mantine/core';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-map-gl';

import {
  ResultsMapColorMode,
  ResultsMapLegendEntry,
} from '../data-access/types/results-map';
import { getLegendEntryTranslationKey } from '../utils/results-map-legend';

import { LegendControl } from '@map';

import classes from './results-map-legend-panel.module.css';

export interface ResultsMapLegendPanelProps {
  colorMode: ResultsMapColorMode;
  onColorModeChange: (mode: ResultsMapColorMode) => void;
  legend: ResultsMapLegendEntry[];
}

function getMapPortalTarget(mapId: string, position: 'top-left' | 'bottom-left') {
  const positionClass =
    position === 'top-left' ? 'mapboxgl-ctrl-top-left' : 'mapboxgl-ctrl-bottom-left';
  return document
    .getElementById(mapId)
    ?.getElementsByClassName(positionClass)[0] as HTMLElement | undefined;
}

export const ResultsMapLegendPanel: FC<ResultsMapLegendPanelProps> = ({
  colorMode,
  onColorModeChange,
  legend,
}) => {
  const { t } = useTranslation();
  const map = useMap();
  const mapId = map.current?.getMap().getContainer().id;

  if (!mapId) {
    return null;
  }

  return (
    <>
      <Portal target={getMapPortalTarget(mapId, 'top-left')}>
        <div className={`${classes.modeToggle} mapboxgl-ctrl`}>
          <SegmentedControl
            data={[
              {
                label: t('sampling.results.map.legend_mode.ges_status'),
                value: 'ges_status',
              },
              {
                label: t('sampling.results.map.legend_mode.carbon'),
                value: 'carbon',
              },
            ]}
            onChange={(value) => onColorModeChange(value as ResultsMapColorMode)}
            size="xs"
            value={colorMode}
          />
        </div>
      </Portal>

      <LegendControl>
        <Stack gap="sm">
          <Text fw={600} size="xs">
            {colorMode === 'carbon'
              ? t('sampling.results.map.legend_title.carbon')
              : t('sampling.results.map.legend_title.ges_status')}
          </Text>
          <Stack gap={6}>
            {legend.map((item) => (
              <Group gap="xs" key={`${item.value}-${item.color}`} wrap="nowrap">
                <span
                  className={classes.swatch}
                  style={{ backgroundColor: item.color }}
                />
                <Text size="xs">
                  {item.value
                    ? t(getLegendEntryTranslationKey(colorMode, item.value), {
                        defaultValue: item.value,
                      })
                    : '—'}
                </Text>
              </Group>
            ))}
          </Stack>
        </Stack>
      </LegendControl>
    </>
  );
};

export default ResultsMapLegendPanel;
